import { computed, inject, Injectable, OnDestroy, Signal, signal } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import { isValidPartnerSlug, normalizePartnerSlug, PartnerContextService } from './partner-context.service';

/**
 * localStorage key holding the authorized session as ONE entry: the partner the panel was
 * signed in to, the API key that partner accepted and the partner record it answered with.
 *
 * Keeping them in a single entry is what makes the pair atomic — with two independent
 * entries a reload (or another tab) could observe one partner's slug next to another
 * partner's key and use the key while looking at the wrong partner's address.
 */
export const SESSION_STORAGE_KEY = 'marketplace_admin_session';

/**
 * Pre-multi-partner storage key holding a bare API key. Sessions created before the partner
 * slug existed are migrated into {@link SESSION_STORAGE_KEY} on first read, so users stay
 * signed in across the upgrade.
 */
export const LEGACY_API_KEY_STORAGE_KEY = 'marketplace_admin_api_key';

/** Partner record returned by `POST /auth/authorize` for the presented API key. */
export interface AuthorizedPartner {
  id: number;
  slug: string;
  name: string;
  status: string;
}

/** Answer of `POST /auth/authorize`. */
export interface AuthorizeResponse {
  message: string;
  status: string;
  partner: AuthorizedPartner;
}

/** An API key together with the partner that accepted it. */
export interface AdminSession {
  slug: string;
  apiKey: string;
  /** Absent only for sessions migrated from before the backend returned the partner. */
  partner?: AuthorizedPartner;
}

/**
 * Outcome of a sign-in attempt that got an answer from the backend.
 *
 * `partner-mismatch` is its own outcome on purpose: the key is perfectly valid, it just
 * belongs to a different partner than the address the user is on, and the panel can point
 * them at the right address instead of claiming the key is wrong.
 */
export type LoginResult =
  | { outcome: 'authorized'; partner: AuthorizedPartner | null }
  | { outcome: 'rejected' }
  | { outcome: 'partner-mismatch'; partner: AuthorizedPartner };

/** Statuses the backend answers with when it does not accept the presented API key. */
const REJECTED_STATUSES: readonly number[] = [401, 403];

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  private readonly http = inject(HttpClient);

  // Injected before the session state below, which reads (and migrates) stored data.
  private readonly partnerContext = inject(PartnerContextService);

  private readonly sessionState = signal<AdminSession | null>(this.readSession());

  /**
   * Partner of the current session, but only while the panel is on that partner's address —
   * a session for partner A never labels partner B's pages. `null` when signed out, when the
   * URL points elsewhere, or for a migrated session that predates the partner record.
   */
  readonly partner: Signal<AuthorizedPartner | null> = computed(() => {
    const session = this.sessionState();

    return session && session.slug === this.partnerContext.slug ? session.partner ?? null : null;
  });

  /**
   * Another tab may sign in as a different partner or sign out; both rewrite the shared
   * session entry. This tab follows it instead of keeping a stale session.
   */
  private readonly onStorageChanged = (event: StorageEvent): void => {
    // A null key means the whole storage was cleared in the other tab.
    if (event.key !== null && event.key !== SESSION_STORAGE_KEY) {
      return;
    }

    this.sessionState.set(this.readSession());
  };

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.onStorageChanged);
    }
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.onStorageChanged);
    }
  }

  /**
   * Authorizes an API key and, only if it belongs to the partner whose address the panel is
   * on, makes it the current session.
   *
   * The backend is one multi-partner service: it resolves the partner from the key itself
   * and answers with that partner. The panel compares it with the slug in its own URL, so a
   * key pasted at the wrong address is reported as such instead of quietly signing the user
   * in as somebody else.
   *
   * Nothing is persisted before the answer arrives: a rejected or mismatched attempt leaves
   * an existing session (and the remembered partner) exactly as it was.
   *
   * @returns the outcome of the attempt. Any failure that is not a rejection — offline,
   *   CORS, 5xx — is rethrown, so the caller can tell "wrong key" from "could not ask".
   */
  login(apiKey: string): Observable<LoginResult> {
    const expectedSlug = this.partnerContext.slug;
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${apiKey}`
    });

    return this.http.post<AuthorizeResponse>(`${this.partnerContext.apiBaseUrl}/auth/authorize`, {}, { headers }).pipe(
      map((response): LoginResult => {
        const partner = this.toPartner(response);

        // An older backend may not name the partner; then the address on screen is all we
        // have to go by and the key is accepted for it.
        if (partner && partner.slug !== expectedSlug) {
          return { outcome: 'partner-mismatch', partner };
        }

        this.startSession({ slug: expectedSlug, apiKey, partner: partner ?? undefined });

        return { outcome: 'authorized', partner };
      }),
      catchError((error: HttpErrorResponse) => {
        if (REJECTED_STATUSES.includes(error.status)) {
          console.error('Authorization rejected:', error);
          return of<LoginResult>({ outcome: 'rejected' });
        }

        // Transient failure: the key was never judged, so the current session survives.
        console.error('Authorization request failed:', error);
        return throwError(() => error);
      })
    );
  }

  /** Ends the session. The remembered partner is kept, so `/` still opens this partner. */
  logout(): void {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(LEGACY_API_KEY_STORAGE_KEY);
    this.sessionState.set(null);
  }

  /**
   * API key to authenticate requests with, or `null` when there is none for the partner whose
   * address the panel is on. A key is never handed out under another partner's address.
   */
  getApiKey(): string | null {
    const session = this.sessionState();

    return session && session.slug === this.partnerContext.slug ? session.apiKey : null;
  }

  /**
   * Persists an authorized session: the slug/key/partner triple first, the remembered
   * partner second. Every reader therefore sees a complete session or none.
   */
  private startSession(session: AdminSession): void {
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    localStorage.removeItem(LEGACY_API_KEY_STORAGE_KEY);
    this.partnerContext.rememberSlug(session.slug);
    this.sessionState.set(session);
  }

  /**
   * Reads the stored session, ignoring anything malformed (hand-edited storage, a partial
   * write, a slug format change) rather than letting it reach a request header.
   */
  private readSession(): AdminSession | null {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);

    if (stored === null) {
      return this.migrateLegacySession();
    }

    try {
      const parsed: unknown = JSON.parse(stored);

      return this.toSession(parsed);
    } catch {
      return null;
    }
  }

  /**
   * Upgrades a session stored before the panel knew about partners: the bare API key belongs
   * to the partner remembered for this browser (the default one when there is none). Runs
   * once — the legacy entry is dropped as soon as it is converted.
   */
  private migrateLegacySession(): AdminSession | null {
    const legacyApiKey = localStorage.getItem(LEGACY_API_KEY_STORAGE_KEY);

    if (!legacyApiKey) {
      return null;
    }

    const session: AdminSession = { slug: this.partnerContext.rememberedSlug, apiKey: legacyApiKey };

    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    localStorage.removeItem(LEGACY_API_KEY_STORAGE_KEY);

    return session;
  }

  /** Type guard turning parsed JSON into a session, or `null` when it is not one. */
  private toSession(value: unknown): AdminSession | null {
    if (typeof value !== 'object' || value === null) {
      return null;
    }

    const { slug, apiKey, partner } = value as Partial<AdminSession>;

    if (typeof slug !== 'string' || typeof apiKey !== 'string') {
      return null;
    }

    if (!isValidPartnerSlug(slug) || apiKey.length === 0) {
      return null;
    }

    return {
      slug: normalizePartnerSlug(slug),
      apiKey,
      ...(this.isPartner(partner) ? { partner } : {})
    };
  }

  /** Reads the partner out of an authorize response, tolerating a backend that omits it. */
  private toPartner(response: AuthorizeResponse | null): AuthorizedPartner | null {
    const partner = response?.partner;

    if (!this.isPartner(partner)) {
      return null;
    }

    return { ...partner, slug: normalizePartnerSlug(partner.slug) };
  }

  /** Type guard for the partner record, so a malformed one is ignored instead of trusted. */
  private isPartner(value: unknown): value is AuthorizedPartner {
    if (typeof value !== 'object' || value === null) {
      return false;
    }

    const { slug, name } = value as Partial<AuthorizedPartner>;

    return typeof slug === 'string' && isValidPartnerSlug(slug) && typeof name === 'string';
  }
}
